import React from "react";

import ChevronDown from "@uui/ChevronDown";
import { sortBy, uniqBy } from "lodash/fp";

import Typography from "@mui/material/Typography";

import FilterCountChip from "@app/analytics/components/FilterCountChip";
import useReportsTable from "@app/analytics/MeetingReports/stores/reportsTable";
import { ReportSource } from "@app/analytics/MeetingReports/types";
import { getReportSourceFilterValue } from "@app/analytics/MeetingReports/utils";
import { LABEL_FOR_CONFERENCING_PLATFORM } from "@app/common/constants/conferencing";
import { PRODUCTIVITY_PROVIDERS } from "@app/common/constants/providers";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import Checkbox from "@rui/Checkbox";
import { Row } from "@rui/Stack";

import { Menu, MenuButton, MenuItem } from "./styles";

const MEETING_SOURCES = Object.entries(LABEL_FOR_CONFERENCING_PLATFORM).map(
  ([platform, label]) => ({
    label,
    value: platform as ReportSource,
  })
);

const TOPIC_SOURCES = PRODUCTIVITY_PROVIDERS.map((provider) => ({
  label: provider.displayNameShort,
  value: getReportSourceFilterValue(provider.id),
}));

const SOURCES: { value: ReportSource; label: string }[] = [
  ...sortBy("label", uniqBy("value", [...MEETING_SOURCES, ...TOPIC_SOURCES])),
  {
    label: "Uploaded Report",
    value: "upload",
  },
];

const SourceFilterMenu = () => {
  const { anchorEl, open, onClose, onClick } = useAnchorElement();

  const { sources, toggleSource } = useReportsTable((state) => ({
    sources: state.sources,
    toggleSource: state.toggleSource,
  }));

  const handleToggleSource = (value: ReportSource | "all") => () => {
    toggleSource(value);
  };

  return (
    <>
      <MenuButton
        aria-controls={open ? "source-filter-menu" : undefined}
        aria-haspopup
        aria-expanded={open ? "true" : undefined}
        onClick={onClick}
        endIcon={<ChevronDown sx={{ fontSize: "20px" }} />}
      >
        <Typography variant="bodySecondaryMed">Source</Typography>
        {sources.selected.length > 0 &&
          sources.selected.length < sources.all.length && (
            <FilterCountChip>{sources.selected.length}</FilterCountChip>
          )}
      </MenuButton>
      <Menu
        id="source-filter-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
      >
        <MenuItem isCategory value="all" onClick={handleToggleSource("all")}>
          <Row variant="apart" width="100%">
            <Typography variant="bodySecondaryMed">Select all</Typography>
            <Checkbox
              variant="filled"
              checked={sources.all.length === sources.selected.length}
            />
          </Row>
        </MenuItem>
        {SOURCES.map((source) => (
          <MenuItem
            key={source.value}
            value={source.value}
            onClick={handleToggleSource(source.value)}
            disabled={!sources.all.includes(source.value)}
          >
            <Row variant="apart" width="100%">
              <Typography variant="bodySecondaryReg">{source.label}</Typography>
              <Checkbox
                variant="filled"
                checked={sources.selected.includes(source.value)}
              />
            </Row>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default SourceFilterMenu;
